/* eslint-disable @nx/enforce-module-boundaries */
// Imports for external libraries go here.
import { FC, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery, useMutation } from '@apollo/client';
import clsx from 'clsx';
import { CUSTOM_EVENTS_CONSTANTS, constants } from './constants';
import { Button, canUseDOM, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { PropertyBarProps, SavedProperty, UpdateSavePropertiesResult } from './PropertyBar.types';
import { FlexAnchor, StyledPropertyBar } from './PropertyBar.styles';
import { propertyBarQuery, propertyBarAuthQuery, updateSaveProperties } from './queries';
import { getSavedProperties, getSubDirectoryLocale, eventUtil } from './utils';
import { GOOGLE_MAP_URL, SIGNIN_URL } from './constants';
import { addSubDirectoryPrefix } from '../../utils';
import { getClickTrackValue } from '@marriott/mi-common-static-components';
import { usePropertyBar } from './use-property-bar';
import { useSession } from '@marriott/mi-headless-utils';
export const PropertyBar: FC<PropertyBarProps> = ({
  hotelLabel,
  phoneLabel,
  mapLabel,
  showNewHotelBadge,
  newHotelBadgeLabel,
  virtualTourLink,
  virtualTourLabel,
  saveLabel,
  savedLabel,
  starsOutOfText,
  locale,
  trackingProperties,
  hideHotelDetails,
  hideSaveButton,
  onServiceError,
}) => {
  //Read ?propertyCode= or ?propertyId= from URL (Pages Router) and uppercase it
  const { query } = useRouter();
  // IMPORTANT: access via bracket notation due to index signature typing
  const qpRaw = query['propertyCode'] as string | string[] | undefined;
  const qpPropertyId = query['propertyId'] as string | undefined;
  const qpPropertyCode = (Array.isArray(qpRaw) ? qpRaw[0] : qpRaw || '').toUpperCase();

  const { setPropertyDetails } = usePropertyBar();
  const { datalayer, session } = useSession();
  const sessionData = canUseDOM ? window?.sessionData || session || {} : {};
  const { isSignedInUser, isRememberedUser } = useUserDetailsGlobalStore(state => ({
    isSignedInUser: state?.isSignedInUser,
    isRememberedUser: state.rememberedUser,
  }));
  const isDesktopView = useCheckBreakpoint('viewportM'); // tablet and nigher
  const { envVarsObject } = useClientEnvVarsStore();
  const urlLocale = locale || envVarsObject?.['ACCEPT_LANGUAGE'];
  // find the property id from the global store with fallback to datalayer window object
  const propertyId =
    (canUseDOM &&
      ((qpPropertyCode ||
        qpPropertyId ||
        session?.data?.AriesSearch?.searchCriteria?.propertyId ||
        datalayer?.['prop_marsha_code'] ||
        datalayer?.['mrshaCode']) as string | undefined)) ||
    'NYCES';

  const [isSavedProperty, setIsSavedProperty] = useState<boolean>(false);
  const variables: { propertyId: string; customerId?: string } = { propertyId: propertyId as string };

  if (isSignedInUser) {
    // in the context of logged in state in the search application the globalStore gives undefined for sessionData because it's being saved
    // as sessionFetchedData. This work around is the least invasive way to ensure we get the ID that we need.
    const customerId =
      canUseDOM && ((sessionData?.data?.consumerID || sessionData?.cacheData?.data?.consumerID) as string | undefined);
    variables.customerId =
      sessionData?.data?.consumerID || sessionData?.cacheData?.data?.consumerID || (customerId as string);
  }

  // fetch data hotel data for either signed in or signed out users.
  const { data, refetch } = useQuery(isSignedInUser ? propertyBarAuthQuery : propertyBarQuery, {
    fetchPolicy: isSignedInUser ? 'no-cache' : 'cache-first',
    context: {
      headers: {
        'accept-language': urlLocale,
      },
    },
    skip: !propertyId,
    variables,
    onCompleted: data => {
      // check to see if the property is saved by the user
      setIsSavedProperty(
        data?.customer?.savedProperties?.find((property: SavedProperty) => property.id === propertyId) !== undefined
      );
    },
    onError: error => {
      console.error('Error fetching property data', error);
      onServiceError?.();
    },
  });
  const { ON_SHOW_HOTEL_DETAIL } = CUSTOM_EVENTS_CONSTANTS;
  const { HOTEL_DETAILS_ID } = constants;
  const [updateCustomerSavedProperties] = useMutation(updateSaveProperties, {
    fetchPolicy: 'no-cache',
    context: {
      headers: {
        'accept-language': urlLocale,
      },
    },
    onCompleted: (data: UpdateSavePropertiesResult) => {
      // check to see if the property is saved by the user
      setIsSavedProperty(
        data?.updateCustomerSavedProperties?.savedProperties?.find(
          (property: SavedProperty) => property.id === propertyId
        ) !== undefined
      );
    },
    onError: error => console.error('Error while updating properties', error),
  });

  const handleSaveProperty = async () => {
    const properties = getSavedProperties(propertyId as string, data?.customer?.savedProperties);
    await updateCustomerSavedProperties({
      variables: {
        input: {
          id: data?.customer?.id,
          revisionToken: data?.customer?.revisionToken,
          savedProperties: properties,
        },
      },
    });
    refetch();
  };
  // example: https://www.marriott.com/en-us/hotels/mspak-emery-autograph-collection/reviews/
  const hotelReviewUrl = addSubDirectoryPrefix(
    `${getSubDirectoryLocale(urlLocale)}/hotels/${data?.property?.seoNickname}/reviews`
  );

  // example: https://www.marriott.com/en-us/hotels/mspak-emery-autograph-collection/overview/
  const hotelDetailUrl = addSubDirectoryPrefix(`/hotels/${data?.property?.seoNickname}/overview`);

  const returnToUrl = () => {
    if (!canUseDOM) return '';
    const currentUrl = new URL(window.location.href);
    return encodeURIComponent(currentUrl.href);
  };

  /**
   * Fire event to show hotel detail passing the review url and the
   * hotel detail url. What listeners of this event do is up to them, most likely
   * open a modal or navigate to a new page.
   */
  const handlePropertyDetail = (event: unknown) => {
    event && (event as Event).preventDefault();
    if (canUseDOM && typeof window !== 'undefined') {
      eventUtil?.dispatch(ON_SHOW_HOTEL_DETAIL, {
        id: HOTEL_DETAILS_ID,
        hotelReviewUrl,
        hotelDetailUrl,
        disableReviews: false,
      });
    }
    setPropertyDetails({ showPropertyDetails: true, propertyDetails: { hotelReviewUrl, hotelDetailUrl } });
    // void push(hotelDetailUrl);
  };
  // Brand code constant for Bulgari
  const BULGARI_BRAND_CODE = 'BG';
  // Extract brand code from UXL response (used for suppression logic)
  const propertyBrandCode = data?.property?.basicInformation?.marshaBrandCode;
  // Reviews link: show only if reviews count is not null and not Bulgari (BG) brand
  const reviewsCount = data?.property?.reviews?.numberOfReviews?.count;
  const showReviewsLink = reviewsCount != null && reviewsCount > 0 && propertyBrandCode !== BULGARI_BRAND_CODE;
  // Map link: suppress when latitude or longitude is not available
  const hasCoordinates = !!data?.property?.basicInformation?.latitude && !!data?.property?.basicInformation?.longitude;
  // Phone link: suppress when phone number is not available
  const hasPhoneNumber = !!data?.property?.contactInformation?.contactNumbers?.[0]?.phoneNumber?.display;

  if (!data?.property) {
    return null;
  }

  // Hotel Details link: suppress when explicitly configured via AEM dialog
  const showHotelDetailsLink = hideHotelDetails !== true && hideHotelDetails !== 'true';

  const normalizedNewHotelBadgeLabel = newHotelBadgeLabel?.trim();
  // New Hotel badge: only render when authored and explicitly enabled in AEM
  const showNewHotelBadgeLabel =
    (showNewHotelBadge === true || showNewHotelBadge === 'true') && Boolean(normalizedNewHotelBadgeLabel);

  // Save button: suppress when explicitly configured via AEM dialog
  const showSaveButton = hideSaveButton !== true && hideSaveButton !== 'true';

  return (
    <StyledPropertyBar className={`brand-css container-lg container-xl`}>
      <div className="wrapper">
        <span className="t-subtitle-xl">{data?.property?.basicInformation?.name}</span>
        <div className="property-info">
          {showNewHotelBadgeLabel && (
            <span className="new-hotel-badge t-font-xs" aria-label={normalizedNewHotelBadgeLabel}>
              {normalizedNewHotelBadgeLabel}
            </span>
          )}
          {showHotelDetailsLink && (
            <Button
              className="transparent-button d-flex align-items-center px-0 icon-city icon-height t-font-weight-r t-foreground-color transparent-button"
              custom_click_track_value={getClickTrackValue({
                trackingProperties,
                fallbacks: {
                  description: 'Hotel Details',
                  position: 'PropertyBar',
                },
              })}
              ariaLabel={hotelLabel}
              callback={handlePropertyDetail}
            >
              <span className="ml-1 t-font-xs">{isDesktopView && hotelLabel}</span>
            </Button>
          )}
          {virtualTourLink && virtualTourLabel && (
            <FlexAnchor
              href={virtualTourLink}
              className="icon-3d-box icon-height t-foreground-color custom_click_track"
              aria-label={virtualTourLabel}
              data-custom_click_track_value={getClickTrackValue({
                trackingProperties,
                fallbacks: {
                  description: 'Virtual Tour',
                  position: 'PropertyBar',
                },
              })}
            >
              <span className="ml-1 t-font-xs">{isDesktopView && virtualTourLabel}</span>
            </FlexAnchor>
          )}
          {hasCoordinates && (
            <FlexAnchor
              href={`${GOOGLE_MAP_URL}${data?.property?.basicInformation?.latitude},${data?.property?.basicInformation?.longitude}`}
              target={'_blank'}
              rel="noopener noreferrer"
              className="icon-location icon-height t-foreground-color custom_click_track"
              data-custom_click_track_value={getClickTrackValue({
                trackingProperties,
                fallbacks: {
                  description: 'Address Link',
                  position: 'PropertyBar',
                },
              })}
            >
              <span className="ml-1 t-font-xs">{isDesktopView && mapLabel}</span>
            </FlexAnchor>
          )}
          {hasPhoneNumber && (
            <FlexAnchor
              href={`tel:${data?.property?.contactInformation?.contactNumbers[0]?.phoneNumber?.original}`}
              aria-label={phoneLabel}
              className="icon-phone icon-height t-foreground-color custom_click_track"
              data-custom_click_track_value={getClickTrackValue({
                trackingProperties,
                fallbacks: {
                  description: 'Phone Number',
                  position: 'PropertyBar',
                },
              })}
            >
              <span className="ml-1 t-font-xs">
                {isDesktopView && data?.property?.contactInformation?.contactNumbers[0]?.phoneNumber?.display}
              </span>
            </FlexAnchor>
          )}
          {showReviewsLink && (
            <div>
              <FlexAnchor
                href={hotelReviewUrl}
                target={'_blank'}
                rel="noreferrer"
                data-custom_click_track_value={getClickTrackValue({
                  trackingProperties,
                  fallbacks: {
                    description: 'Ratings',
                    position: 'PropertyBar',
                  },
                })}
                aria-label={`${data?.property?.reviews?.stars?.count} ${starsOutOfText} ${data?.property?.reviews?.numberOfReviews?.count}`}
                className="icon-star-fill icon-height t-foreground-color custom_click_track"
              >
                <span className="ml-1 t-font-xs">{data?.property?.reviews?.stars?.count}</span>
                <span className="t-font-xs rating-count">{`(${data?.property?.reviews?.numberOfReviews?.count})`}</span>
              </FlexAnchor>
            </div>
          )}
          {showSaveButton && isSignedInUser && data?.customer?.id && (
            <Button
              className={clsx(
                'transparent-button d-flex align-items-center icon-height  t-foreground-color',
                isSavedProperty ? 'icon-heart-fill' : 'icon-heart-outline'
              )}
              ariaLabel={isSavedProperty ? savedLabel : saveLabel}
              callback={handleSaveProperty}
            >
              <span className="ml-1 t-font-xs">{isDesktopView && (isSavedProperty ? savedLabel : saveLabel)}</span>
            </Button>
          )}
          {showSaveButton && isRememberedUser && (
            <FlexAnchor
              className={clsx(
                'icon-height  t-foreground-color',
                isSavedProperty ? 'icon-heart-fill' : 'icon-heart-outline'
              )}
              href={`${addSubDirectoryPrefix(SIGNIN_URL)}?returnTo=${returnToUrl()}`}
            >
              <span className="ml-1 t-font-xs">{isSavedProperty ? savedLabel : saveLabel}</span>
            </FlexAnchor>
          )}
        </div>
      </div>
    </StyledPropertyBar>
  );
};
